import { rtk } from "./generated.ts";
import { getAuthToken, handleUnauthorized } from "../utils/authSession.js";

const normalizeConversationsResponse = (
  data?: MarketplaceConversationsListResponse,
): MarketplaceConversation[] => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.conversations)) return data.conversations;
  return [];
};

const enhancedRtkApi = rtk.enhanceEndpoints({
  addTagTypes: ["profile", "MarketplaceProduct", "MarketplaceChat", "Board", "BoardPin", "Brand", "Aesthetic"],
  endpoints: {
    getExclusiveProductsAll: {
      providesTags: (result) => [
        {
          type: "MarketplaceProduct",
          id: "exclusive-list",
        },
        ...((result?.results || []).map((product) => ({
          type: "MarketplaceProduct" as const,
          id: `exclusive-${product.id}`,
        }))),
      ],
      keepUnusedDataFor: 300,
    },
    postClaimSurvey: {
      invalidatesTags: ["profile", "MarketplaceProduct"],
    },
    getUserMarketplaceSuggestions: {
      providesTags: ["MarketplaceProduct"],
    },
    profileMeasurementsStoreUpdate: {
      invalidatesTags: ["profile"],
    },
    profileMeasurementsUpdateUpdate: {
      invalidatesTags: ["profile"],
    },
    profileMeasurementsUpdate: {
      invalidatesTags: ["profile"],
    },
    profileUpdateUpdate: {
      invalidatesTags: ["profile"],
    },
    getMyBodyType: {
      providesTags: ["profile"],
      keepUnusedDataFor: 300,
    },
    getMyFaceShape: {
      providesTags: ["profile"],
      keepUnusedDataFor: 300,
    },
    getMyColorAnalysis: {
      providesTags: ["profile"],
      keepUnusedDataFor: 300,
    },
    profileRead: {
      providesTags: ["profile"],
    },
    getProductsAllProducts: {
      providesTags: ["MarketplaceProduct"],
      keepUnusedDataFor: 300,
    },
    publicProductsAllList: {
      providesTags: ["MarketplaceProduct"],
      keepUnusedDataFor: 300,
    },
    postSaveBoard: {
      invalidatesTags: [{ type: "Board", id: "LIST" }],
    },
    postSavePin: {
      invalidatesTags: [
        { type: "Board", id: "LIST" },
        { type: "BoardPin", id: "LIST" },
      ],
    },
    deleteDeletePin: {
      invalidatesTags: [
        { type: "Board", id: "LIST" },
        { type: "BoardPin", id: "LIST" },
      ],
    },
    postCreateBoard: {
      invalidatesTags: [{ type: "Board", id: "LIST" }],
    },
    postEditBoard: {
      invalidatesTags: [{ type: "Board", id: "LIST" }],
    },
    getViewBoards: {
      providesTags: [{ type: "Board", id: "LIST" }],
      keepUnusedDataFor: 0,
    },
    postGetPins: {
      invalidatesTags: [],
    },
    postMoreProductsSearch: {
      transformResponse: (response: any, _meta: unknown, arg: any) => {
        const query = arg?.moreProductsSearch?.query || "";
        const slugs = Array.isArray(arg?.moreProductsSearch?.slugs)
          ? arg.moreProductsSearch.slugs
          : [];
        const normalizedQuery = String(query).trim().toLowerCase();

        const normalizedSlugs = [...slugs]
          .map((slug) => String(slug || "").trim())
          .filter(Boolean)
          .sort()
          .join(",");

        return {
          ...response,
          requestSignature: `${normalizedQuery}::${normalizedSlugs}`
        };
      },
      invalidatesTags: [],
    },
  },
});

type MarketplaceProductDetailsArg = {
  product_id?: number;
  slug?: string;
  match_percentage?: number | null;
  product_type?: ProductType;
  page?: number;
  page_size?: number;
};

type UserTopProductItem = {
  id?: string | number;
  slug?: string;
  product_url?: string;
  [key: string]: unknown;
};

type UserTopProductsResponse = {
  status?: string;
  product_id?: number;
  similar_products?: UserTopProductItem[];
  page?: number;
  page_size?: number;
  total?: number;
  has_more?: boolean;
  next_page?: number | null;
  max_pool_size?: number;
  source?: string;
};

type MarketplaceListingProduct = {
  id?: string | number;
  name?: string;
  product_name?: string;
  slug?: string;
  real_slug?: string;
  url?: string;
  product_url?: string;
  image_url?: string;
  price?: string | number;
  garment_type?: string;
  brand?: string;
  match_percentage?: number | null;
  perfect_match?: boolean;
  [key: string]: unknown;
};

type MarketplaceProductsPage = {
  status?: string;
  message?: string;
  query_used?: string;
  source?: string;
  products?: MarketplaceListingProduct[];
  page?: number;
  page_size?: number;
  total?: number;
  total_products?: number;
  has_more?: boolean;
  next_page?: number | null;
  max_pool_size?: number;
};

type MarketplaceSearchCachedArg = {
  text?: string;
  page?: number;
  page_size?: number;
  owner_key?: string;
  brand?: string | number;
  gender?: string | number;
  region?: string;
  garmentType?: string;
  sort?: string;
  occasionType?: string;
  categoriesType?: string;
  fitType?: string;
  sizeType?: string;
  color?: string;
  aesthetic?: string;
  sustainability?: string;
  minPrice?: number;
  maxPrice?: number;
};

type MarketplaceSuggestionsCachedArg = {
  page?: number;
  page_size?: number;
  owner_key?: string;
};

type PublicMarketplaceSuggestionsResponse =
  | MarketplaceListingProduct[]
  | MarketplaceProductsPage;

type UserSingleMarketplaceProductStreamArg = {
  product_id?: number;
  slug?: string;
  match_percentage?: number | null;
  product_type?: ProductType;
  token?: string;
  request_key?: string;
};

type UserSingleMarketplaceProductStreamState = {
  product_id: number;
  recommended_size: string | null;
  match_percentage: number | null;
  match_summary: string;
  tags: string[];
  done: boolean;
  error?: string;
};

type StreamEventData = {
  status?: string;
  recommended_size?: string | null;
  match_percentage?: number | string | null;
  match_summary?: string | null;
  tags?: string[];
  message?: string;
  detail?: string;
  b64?: string;
  index?: number;
};

type StreamEventPayload = StreamEventData & {
  type?: string;
  data?: StreamEventData;
};

type MultiGarmentTryOnStreamArg = {
  person_url: string;
  selected_size: string;
  product_slug: string;
  product_type: ProductType;
  pins: string[];
  request_id: string;
};

type MultiGarmentTryOnStreamState = {
  request_id: string;
  status: string;
  message: string;
  thoughts: string[];
  partial_image: string;
  final_image: string;
  done: boolean;
  error?: string;
};

type DeleteMarketplaceConversationArg = {
  conversation_id: string;
};

type DeleteMarketplaceConversationResponse = {
  message?: string;
  detail?: string;
};

type MarketplaceConversation = {
  id?: string | number;
  conversation_id?: string | number;
  title?: string;
  description?: string;
  name?: string;
  last_message?: string;
  created_at?: string;
  updated_at?: string;
};

type MarketplaceConversationsListResponse =
  | MarketplaceConversation[]
  | {
      results?: MarketplaceConversation[];
      conversations?: MarketplaceConversation[];
    };

type MarketplaceConversationMessagesArg = {
  conversation_id: string;
};

type MarketplaceChatProduct = {
  id?: string | number;
  name?: string;
  price?: string | number;
  slug?: string;
  image_url?: string;
  product_score?: number | string | null;
  brand?: string;
  why_this_works?: string;
  what_to_avoid?: string;
};

type MarketplaceConversationMessage = {
  role: "user" | "assistant" | string;
  content?: string;
  image_url?: string | null;
  products?: MarketplaceChatProduct[];
};

type MarketplaceConversationMessagesResponse = {
  messages?: MarketplaceConversationMessage[];
};

type GetPinsCachedArg = {
  board_slug: string;
};

type DeletePinWithBodyArg = {
  board_slug: string;
  slug: string;
  product_type: ProductType;
};

type DeletePinWithBodyResponse = {
  detail?: string;
};

type BrandSummary = {
  id: number;
  name: string;
  slug: string;
};

type BrandInformation = {
  id: number;
  slug: string;
  name: string;
  title: string;
  description: string;
  about: string;
  sizing_and_fit: string;
  what_to_know: string;
  image_url: string;
};

type BrandProduct = {
  id: number;
  product_name: string;
  slug: string;
  product_url: string;
  image_url: string;
  price: string | number;
  garment_type?: string;
  match_percentage?: number | null;
  brand?: string;
};

type BrandLookupArg = {
  slug?: string;
  id?: number;
  forceRefresh?: boolean;
};

type AestheticSummary = {
  id: number;
  name: string;
  slug: string;
};

type AestheticWhyAtEvenessItem = {
  title: string;
  description: string;
};

type AestheticInformation = {
  id: number;
  slug: string;
  name: string;
  title: string;
  description: string;
  why_at_eveness?: AestheticWhyAtEvenessItem[];
  shop_with_heading?: string;
  shop_with_description?: string;
  image_url?: string;
};

type AestheticProduct = {
  id: number;
  product_name: string;
  slug: string;
  product_url: string;
  image_url: string;
  price: string | number;
  garment_type?: string;
  match_percentage?: number | null;
  brand?: string;
  aesthetic?: string;
  [key: string]: unknown;
};

type AestheticLookupArg = {
  slug?: string;
  id?: number;
  forceRefresh?: boolean;
};

const getAestheticLookupBody = (arg: AestheticLookupArg) => {
  if (arg.slug) return { slug: arg.slug };
  return { id: arg.id };
};

const getAestheticLookupKey = (arg: AestheticLookupArg) =>
  String(arg.slug || arg.id || "")
    .trim()
    .toLowerCase();

const getBrandLookupBody = (arg: BrandLookupArg) => {
  if (arg.slug) {
    return { slug: arg.slug };
  }

  return { id: arg.id };
};

const getBrandLookupKey = (arg: BrandLookupArg) =>
  String(arg.slug || arg.id || "").trim().toLowerCase();

let activeLoggedInMatchStreamController: AbortController | null = null;
let activeMultiGarmentTryOnController: AbortController | null = null;

const getProductLookupBody = (arg: MarketplaceProductDetailsArg) => {
  if (arg.slug) {
    return {
      slug: arg.slug,
      product_type: arg.product_type ?? "marketplace",
      ...(arg.match_percentage !== null && arg.match_percentage !== undefined
        ? { match_percentage: arg.match_percentage }
        : {}),
    };
  }

  return {
    product_id: arg.product_id,
    product_type: arg.product_type ?? "marketplace",
    ...(arg.match_percentage !== null && arg.match_percentage !== undefined
      ? { match_percentage: arg.match_percentage }
      : {}),
  };
};

const getProductLookupKey = (arg: MarketplaceProductDetailsArg) => {
  if (arg.slug) {
    return `${arg.product_type ?? "marketplace"}-${arg.slug}`;
  }

  return `${arg.product_type ?? "marketplace"}-${arg.product_id}`;
};

const getMarketplaceListingProductKey = (item: MarketplaceListingProduct) =>
  String(
    item.id ??
      item.slug ??
      item.real_slug ??
      item.product_url ??
      item.url ??
      "",
  );

const getMarketplaceSearchCacheKey = (arg: MarketplaceSearchCachedArg) =>
  JSON.stringify({
    ownerKey: arg.owner_key ?? "",
    brand: String(arg.brand ?? "").trim().toLowerCase(),
    text: String(arg.text || "").trim(),
    gender: arg.gender ?? null,
    region: arg.region ?? "",
    garmentType: arg.garmentType ?? "",
    sort: arg.sort ?? "",
    occasionType: arg.occasionType ?? "",
    categoriesType: arg.categoriesType ?? "",
    fitType: arg.fitType ?? "",
    sizeType: arg.sizeType ?? "",
    color: arg.color ?? "",
    aesthetic: arg.aesthetic ?? "",
    sustainability: arg.sustainability ?? "",
    minPrice: arg.minPrice ?? null,
    maxPrice: arg.maxPrice ?? null,
    pageSize: arg.page_size ?? 20,
  });

type ProductType = "marketplace" | "product" | "exclusive";

type ExclusiveProductVariant = {
  id: number;
  size: string;
  color?: string;
  price: string | number;
  quantity: number;
  discount?: number;
  discounted_price?: string | number;
};

type ExclusiveProduct = {
  id: number;
  name: string;
  slug: string;
  created_date?: string;
  quantity: number;
  price: string | number;
  discount?: number;
  discounted_price?: string | number;
  image?: string;
  distributor?: string;
  category?: string;
  variants?: ExclusiveProductVariant[];
};

const buildApiUrl = (path: string) => {
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

  if (!baseUrl) {
    return `/api${path}`;
  }

  if (baseUrl.endsWith("/api")) {
    return `${baseUrl}${path}`;
  }

  return `${baseUrl}/api${path}`;
};

const normalizeStreamMatchPayload = (
  payload?: StreamEventPayload["data"],
): Pick<
  UserSingleMarketplaceProductStreamState,
  "recommended_size" | "match_percentage" | "match_summary" | "tags"
> => {
  const rawRecommendedSize = payload?.recommended_size;
  const rawPercentage = payload?.match_percentage;

  return {
    recommended_size:
      rawRecommendedSize !== undefined &&
      rawRecommendedSize !== null &&
      rawRecommendedSize !== ""
        ? String(rawRecommendedSize).trim().toUpperCase()
        : null,
    match_percentage:
      rawPercentage !== undefined &&
      rawPercentage !== null &&
      rawPercentage !== ""
        ? Math.round(Number(rawPercentage))
        : null,
    match_summary:
      typeof payload?.match_summary === "string" ? payload.match_summary : "",
    tags: Array.isArray(payload?.tags) ? payload.tags : [],
  };
};

const getStreamEventData = (payload: StreamEventPayload): StreamEventData => {
  if (payload?.data && typeof payload.data === "object") {
    return {
      ...payload,
      ...payload.data,
    };
  }
  return payload;
};

const toBase64ImageDataUrl = (value?: string) => {
  const compactValue = String(value || "").replace(/\s+/g, "");
  if (!compactValue) return "";
  if (compactValue.startsWith("data:image/")) return compactValue;
  const mimeType = compactValue.startsWith("iVBOR")
    ? "image/png"
    : compactValue.startsWith("UklGR")
      ? "image/webp"
      : compactValue.startsWith("R0lGOD")
        ? "image/gif"
        : "image/jpeg";
  return `data:${mimeType};base64,${compactValue}`;
};

const parseSseBlock = (block: string): StreamEventPayload | null => {
  const trimmed = block.trim();

  if (!trimmed) return null;

  // Handles pasted/single-line style:
  // event: partial data: {...}
  const singleLineMatch = trimmed.match(/^event:\s*(\S+)\s+data:\s*([\s\S]*)$/);

  if (singleLineMatch) {
    const [, eventName, jsonText] = singleLineMatch;

    try {
      const parsed = JSON.parse(jsonText.trim());
      return {
        ...parsed,
        type: parsed?.type || eventName,
      };
    } catch {
      return null;
    }
  }

  // Handles normal SSE style:
  // event: partial
  // data: {...}
  const lines = trimmed.split(/\r?\n/);
  let eventName = "";
  const dataLines: string[] = [];

  for (const line of lines) {
    if (line.startsWith("event:")) {
      eventName = line.slice("event:".length).trim();
    }

    if (line.startsWith("data:")) {
      dataLines.push(line.slice("data:".length).trim());
    }
  }

  const jsonText = dataLines.join("\n").trim();

  if (!jsonText) return null;

  try {
    const parsed = JSON.parse(jsonText);

    return {
      ...parsed,
      type: parsed?.type || eventName,
    };
  } catch {
    return null;
  }
};

const LEGACY_BRAND_PRODUCTS_CACHE_PREFIX = "eveness-brand-products-v1:";
const clearLegacyBrandProductsLocalStorage = () => {
  if (typeof window === "undefined") return;

  try {
    for (
      let index = window.localStorage.length - 1;
      index >= 0;
      index -= 1
    ) {
      const key = window.localStorage.key(index);
      if (key?.startsWith(LEGACY_BRAND_PRODUCTS_CACHE_PREFIX)) {
        window.localStorage.removeItem(key);
      }
    }
  } catch {}
};
clearLegacyBrandProductsLocalStorage();

const marketplaceCachedApi = enhancedRtkApi.injectEndpoints({
  endpoints: (build) => ({
    // ---- Quick pay (Apple Pay / Google Pay) ----

    // The publishable key, so the storefront needs no extra env var and cannot
    // drift out of sync with whichever Stripe mode the backend is running in.
    paymentsConfig: build.query<{ publishableKey: string }, void>({
      query: () => ({ url: `/payments/config/` }),
      keepUnusedDataFor: 3600,
    }),

    // Creates the Order + PaymentIntent. Called from the wallet's onConfirm,
    // never on page load, so browsing never leaves Unpaid orders behind.
    //
    // Two shapes, chosen by where the button sits. Express placements send a
    // `confirmation_token_id` and nothing else; the payer is read off the token
    // server-side, and anything else sent here is ignored. The payment-step
    // placement sends the payer fields its form already collected.
    quickPayCreateIntent: build.mutation<
      {
        clientSecret: string;
        orderId: string;
        amount: number;
        currency: string;
      },
      {
        items: Array<{ variant_id: number; quantity: number }>;
        bundles?: Array<{
          bundle_id?: number;
          slug?: string;
          quantity: number;
          variant_ids: number[];
        }>;
        confirmation_token_id?: string;
        email?: string;
        address?: string;
        first_name?: string;
        last_name?: string;
        phone?: string;
      }
    >({
      query: (body) => ({
        url: `/quick-pay/create-intent/`,
        method: "POST",
        body,
      }),
    }),

    // Confirmation-page lookup for quick pay. Finalises the order against
    // Stripe if the webhook has not landed yet, so the shopper never waits.
    quickPayStatus: build.query<any, { orderId: string }>({
      query: ({ orderId }) => ({ url: `/quick-pay/status/${orderId}/` }),
    }),

    publicMarketplaceSearchCached: build.query<
      MarketplaceProductsPage,
      MarketplaceSearchCachedArg
    >({
      query: (arg) => {
        const body = new FormData();

        body.append("text", String(arg.text || ""));
        body.append("page", String(arg.page ?? 1));
        body.append("page_size", String(arg.page_size ?? 20));
        if (arg.aesthetic !== undefined && arg.aesthetic !== null && String(arg.aesthetic).trim()) {
          body.append("aesthetic", String(arg.aesthetic).trim());
        }
        if (
          arg.brand !== undefined &&
          arg.brand !== null &&
          String(arg.brand).trim()
        ) {
          body.append("brand", String(arg.brand).trim());
        }

        return {
          url: `/public-marketplace-search/`,
          method: "POST",
          body,
          params: {
            gender: arg.gender,
            region: arg.region,
            garment_type: arg.garmentType,
            sort: arg.sort,
            occasion_type: arg.occasionType,
            categories_type: arg.categoriesType,
            fit_type: arg.fitType,
            size_type: arg.sizeType,
            color: arg.color,
            sustainability: arg.sustainability,
            min_price: arg.minPrice,
            max_price: arg.maxPrice,
          },
        };
      },

      serializeQueryArgs: ({ queryArgs }) =>
        `public-marketplace-search-${getMarketplaceSearchCacheKey(queryArgs)}`,

      merge: (currentCache, newResponse, { arg }) => {
        const incomingProducts = Array.isArray(newResponse.products)
          ? newResponse.products
          : [];

        if ((arg.page ?? 1) === 1) {
          Object.assign(currentCache, newResponse);
          currentCache.products = [...incomingProducts];
          return;
        }

        const existingProducts = Array.isArray(currentCache.products)
          ? currentCache.products
          : [];

        const existingKeys = new Set(
          existingProducts
            .map(getMarketplaceListingProductKey)
            .filter(Boolean),
        );

        const uniqueIncomingProducts = incomingProducts.filter((item) => {
          const key = getMarketplaceListingProductKey(item);

          if (!key) return true;
          if (existingKeys.has(key)) return false;

          existingKeys.add(key);
          return true;
        });

        currentCache.products = [
          ...existingProducts,
          ...uniqueIncomingProducts,
        ];

        const {
          products: _ignoredProducts,
          ...latestPaginationMetadata
        } = newResponse;

        Object.assign(currentCache, latestPaginationMetadata);
      },

      forceRefetch: ({ currentArg, previousArg, endpointState }) => {
        const cachedPage = Number(
          (endpointState?.data as MarketplaceProductsPage | undefined)?.page ??
            0,
        );
        const requestedPage = currentArg?.page ?? 1;

        return (
          requestedPage > cachedPage ||
          currentArg?.page_size !== previousArg?.page_size
        );
      },

      providesTags: (_result, _error, arg) => [
        {
          type: "MarketplaceProduct",
          id: `public-search-${getMarketplaceSearchCacheKey(arg)}`,
        },
      ],
      keepUnusedDataFor: 300,
    }),

    userMarketplaceSearchCached: build.query<
      MarketplaceProductsPage,
      MarketplaceSearchCachedArg
    >({
      query: (arg) => {
        const body = new FormData();

        body.append("text", String(arg.text || ""));
        body.append("page", String(arg.page ?? 1));
        body.append("page_size", String(arg.page_size ?? 20));
        if (arg.aesthetic !== undefined &&arg.aesthetic !== null && String(arg.aesthetic).trim()) {
          body.append("aesthetic", String(arg.aesthetic).trim());
        }
        if (
          arg.brand !== undefined &&
          arg.brand !== null &&
          String(arg.brand).trim()
        ) {
          body.append("brand", String(arg.brand).trim());
        }

        return {
          url: `/user-marketplace-search/`,
          method: "POST",
          body,
          params: {
            gender: arg.gender,
            region: arg.region,
            garment_type: arg.garmentType,
            sort: arg.sort,
            occasion_type: arg.occasionType,
            categories_type: arg.categoriesType,
            fit_type: arg.fitType,
            size_type: arg.sizeType,
            color: arg.color,
            sustainability: arg.sustainability,
            min_price: arg.minPrice,
            max_price: arg.maxPrice,
          },
        };
      },

      serializeQueryArgs: ({ queryArgs }) =>
        `user-marketplace-search-${getMarketplaceSearchCacheKey(queryArgs)}`,

      merge: (currentCache, newResponse, { arg }) => {
        const incomingProducts = Array.isArray(newResponse.products)
          ? newResponse.products
          : [];

        if ((arg.page ?? 1) === 1) {
          Object.assign(currentCache, newResponse);
          currentCache.products = [...incomingProducts];
          return;
        }

        const existingProducts = Array.isArray(currentCache.products)
          ? currentCache.products
          : [];

        const existingKeys = new Set(
          existingProducts
            .map(getMarketplaceListingProductKey)
            .filter(Boolean),
        );

        const uniqueIncomingProducts = incomingProducts.filter((item) => {
          const key = getMarketplaceListingProductKey(item);

          if (!key) return true;
          if (existingKeys.has(key)) return false;

          existingKeys.add(key);
          return true;
        });

        currentCache.products = [
          ...existingProducts,
          ...uniqueIncomingProducts,
        ];

        const {
          products: _ignoredProducts,
          ...latestPaginationMetadata
        } = newResponse;

        Object.assign(currentCache, latestPaginationMetadata);
      },

      forceRefetch: ({ currentArg, previousArg, endpointState }) => {
        const cachedPage = Number(
          (endpointState?.data as MarketplaceProductsPage | undefined)?.page ??
            0,
        );
        const requestedPage = currentArg?.page ?? 1;

        return (
          requestedPage > cachedPage ||
          currentArg?.page_size !== previousArg?.page_size
        );
      },

      providesTags: (_result, _error, arg) => [
        {
          type: "MarketplaceProduct",
          id: `user-search-${getMarketplaceSearchCacheKey(arg)}`,
        },
      ],
      keepUnusedDataFor: 300,
    }),

    userMarketplaceSuggestionsCached: build.query<
      MarketplaceProductsPage,
      MarketplaceSuggestionsCachedArg
    >({
      query: (arg) => ({
        url: `/user-marketplace-suggestions/`,
        method: "GET",
        params: {
          page: arg.page ?? 1,
          page_size: arg.page_size ?? 20,
        },
      }),

      serializeQueryArgs: ({ queryArgs }) =>
        `user-marketplace-suggestions-${queryArgs.owner_key || "current-user"}-page-size-${queryArgs.page_size ?? 20}`,

      merge: (currentCache, newResponse, { arg }) => {
        const incomingProducts = Array.isArray(newResponse.products)
          ? newResponse.products
          : [];

        if ((arg.page ?? 1) === 1) {
          Object.assign(currentCache, newResponse);
          currentCache.products = [...incomingProducts];
          return;
        }

        const existingProducts = Array.isArray(currentCache.products)
          ? currentCache.products
          : [];

        const existingKeys = new Set(
          existingProducts
            .map(getMarketplaceListingProductKey)
            .filter(Boolean),
        );

        const uniqueIncomingProducts = incomingProducts.filter((item) => {
          const key = getMarketplaceListingProductKey(item);

          if (!key) return true;
          if (existingKeys.has(key)) return false;

          existingKeys.add(key);
          return true;
        });

        currentCache.products = [
          ...existingProducts,
          ...uniqueIncomingProducts,
        ];

        const {
          products: _ignoredProducts,
          ...latestPaginationMetadata
        } = newResponse;

        Object.assign(currentCache, latestPaginationMetadata);
      },

      forceRefetch: ({ currentArg, previousArg, endpointState }) => {
        const cachedPage = Number(
          (endpointState?.data as MarketplaceProductsPage | undefined)?.page ??
            0,
        );
        const requestedPage = currentArg?.page ?? 1;

        return (
          requestedPage > cachedPage ||
          currentArg?.page_size !== previousArg?.page_size ||
          currentArg?.owner_key !== previousArg?.owner_key
        );
      },

      providesTags: (_result, _error, arg) => [
        {
          type: "MarketplaceProduct",
          id: `user-suggestions-${arg.owner_key || "current-user"}`,
        },
      ],
      keepUnusedDataFor: 300,
    }),

    publicMarketplaceSuggestionsCached: build.query<
      PublicMarketplaceSuggestionsResponse,
      void
    >({
      query: () => ({
        url: `/public-marketplace-suggestions/`,
        method: "GET",
      }),
      serializeQueryArgs: () => "public-marketplace-suggestions",
      providesTags: [
        {
          type: "MarketplaceProduct",
          id: "public-suggestions",
        },
      ],
      keepUnusedDataFor: 300,
    }),

    marketplaceProductDetailsCached: build.query<
      unknown,
      MarketplaceProductDetailsArg
    >({
      query: (arg) => ({
        url: `/singe-marketplace-product-details/`,
        method: "POST",
        body: getProductLookupBody(arg),
      }),
      providesTags: (_result, _error, arg) => [
        {
          type: "MarketplaceProduct",
          id: `details-${getProductLookupKey(arg)}`,
        },
      ],
      keepUnusedDataFor: 300,
    }),

    exclusiveTopProductsCached: build.query<
      ExclusiveProduct[],
      MarketplaceProductDetailsArg
    >({
      query: (arg) => ({
        url: `/exclusive-top-matches/`,
        method: "POST",
        body: {
          slug: arg.slug,
        },
      }),
      transformResponse: (response: unknown) =>
        Array.isArray(response) ? (response as ExclusiveProduct[]) : [],
      providesTags: (_result, _error, arg) => [
        {
          type: "MarketplaceProduct",
          id: `exclusive-top-${arg.slug}`,
        },
      ],
      keepUnusedDataFor: 300,
    }),

    publicCollectionTopProductsCached: build.query<
      unknown,
      MarketplaceProductDetailsArg
    >({
      query: (arg) => ({
        url: `/public-collection-top-matches/`,
        method: "POST",
        body: {
          slug: arg.slug,
        },
      }),
      providesTags: (_result, _error, arg) => [
        {
          type: "MarketplaceProduct",
          id: `public-collection-top-${arg.slug}`,
        },
      ],
      keepUnusedDataFor: 300,
    }),

    userCollectionTopProductsCached: build.query<
      unknown,
      MarketplaceProductDetailsArg
    >({
      query: (arg) => ({
        url: `/collection-top-matches/`,
        method: "POST",
        body: {
          slug: arg.slug,
        },
      }),
      providesTags: (_result, _error, arg) => [
        {
          type: "MarketplaceProduct",
          id: `user-collection-top-${arg.slug}`,
        },
      ],
      keepUnusedDataFor: 300,
    }),

    publicTopProductsCached: build.query<
      UserTopProductsResponse,
      MarketplaceProductDetailsArg
    >({
      query: (arg) => ({
        url: `/public-top-products/`,
        method: "POST",
        body: {
          ...getProductLookupBody({
            ...arg,
            product_type: "marketplace",
          }),
          page: arg.page ?? 1,
          page_size: arg.page_size ?? 20,
        },
      }),

      serializeQueryArgs: ({ queryArgs }) =>
        `public-top-${getProductLookupKey({
          ...queryArgs,
          product_type: "marketplace",
        })}-page-size-${queryArgs.page_size ?? 20}`,

      merge: (currentCache, newResponse, { arg }) => {
        const incomingProducts = Array.isArray(newResponse.similar_products)
          ? newResponse.similar_products
          : [];

        if ((arg.page ?? 1) === 1) {
          Object.assign(currentCache, newResponse);
          currentCache.similar_products = [...incomingProducts];
          return;
        }

        const existingProducts = Array.isArray(currentCache.similar_products)
          ? currentCache.similar_products
          : [];

        const existingProductKeys = new Set(
          existingProducts
            .map((item) =>
              String(item.id ?? item.slug ?? item.product_url ?? ""),
            )
            .filter(Boolean),
        );

        const uniqueIncomingProducts = incomingProducts.filter((item) => {
          const key = String(
            item.id ?? item.slug ?? item.product_url ?? "",
          );

          if (!key) return true;
          if (existingProductKeys.has(key)) return false;

          existingProductKeys.add(key);
          return true;
        });

        currentCache.similar_products = [
          ...existingProducts,
          ...uniqueIncomingProducts,
        ];

        const {
          similar_products: _ignoredSimilarProducts,
          ...latestPaginationMetadata
        } = newResponse;

        Object.assign(currentCache, latestPaginationMetadata);
      },

      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.page !== previousArg?.page ||
        currentArg?.page_size !== previousArg?.page_size,

      providesTags: (_result, _error, arg) => [
        {
          type: "MarketplaceProduct",
          id: `public-top-${getProductLookupKey({
            ...arg,
            product_type: "marketplace",
          })}`,
        },
      ],
      keepUnusedDataFor: 300,
    }),

    userTopProductsCached: build.query<
      UserTopProductsResponse,
      MarketplaceProductDetailsArg
    >({
      query: (arg) => ({
        url: `/user-top-products/`,
        method: "POST",
        body: {
          ...getProductLookupBody({
            ...arg,
            product_type: "marketplace",
          }),
          page: arg.page ?? 1,
          page_size: arg.page_size ?? 20,
        },
      }),

      serializeQueryArgs: ({ queryArgs }) =>
        `user-top-${getProductLookupKey({
          ...queryArgs,
          product_type: "marketplace",
        })}-page-size-${queryArgs.page_size ?? 20}`,

      merge: (currentCache, newResponse, { arg }) => {
        const incomingProducts = Array.isArray(newResponse.similar_products)
          ? newResponse.similar_products
          : [];

        if ((arg.page ?? 1) === 1) {
          Object.assign(currentCache, newResponse);
          currentCache.similar_products = [...incomingProducts];
          return;
        }

        const existingProducts = Array.isArray(currentCache.similar_products)
          ? currentCache.similar_products
          : [];

        const existingProductKeys = new Set(
          existingProducts
            .map((item) =>
              String(item.id ?? item.slug ?? item.product_url ?? ""),
            )
            .filter(Boolean),
        );

        const uniqueIncomingProducts = incomingProducts.filter((item) => {
          const key = String(
            item.id ?? item.slug ?? item.product_url ?? "",
          );

          if (!key) return true;
          if (existingProductKeys.has(key)) return false;

          existingProductKeys.add(key);
          return true;
        });

        currentCache.similar_products = [
          ...existingProducts,
          ...uniqueIncomingProducts,
        ];

        const {
          similar_products: _ignoredSimilarProducts,
          ...latestPaginationMetadata
        } = newResponse;

        Object.assign(currentCache, latestPaginationMetadata);
      },

      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.page !== previousArg?.page ||
        currentArg?.page_size !== previousArg?.page_size,

      providesTags: (_result, _error, arg) => [
        {
          type: "MarketplaceProduct",
          id: `user-top-${getProductLookupKey({
            ...arg,
            product_type: "marketplace",
          })}`,
        },
      ],
      keepUnusedDataFor: 300,
    }),

    userSingleMarketplaceProductCached: build.query<
      unknown,
      MarketplaceProductDetailsArg
    >({
      query: ({ product_id, match_percentage }) => ({
        url: `/user-single-marketplace-product/`,
        method: "POST",
        body: {
          product_id,
          ...(match_percentage !== null && match_percentage !== undefined
            ? { match_percentage }
            : {}),
        },
      }),
      providesTags: (_result, _error, arg) => [
        {
          type: "MarketplaceProduct",
          id: `user-match-${arg.product_id}-${arg.match_percentage ?? "none"}`,
        },
      ],
      keepUnusedDataFor: 300,
    }),
    getPinsCached: build.query<unknown, GetPinsCachedArg>({
      query: ({ board_slug }) => ({
        url: `/get-pins/`,
        method: "POST",
        body: {
          board_slug,
        },
      }),

      serializeQueryArgs: ({ queryArgs }) =>
        `get-pins-${String(queryArgs.board_slug || "").trim()}`,

      providesTags: (_result, _error, arg) => [
        { type: "BoardPin", id: "LIST" },
        { type: "BoardPin", id: `board-${arg.board_slug}` },
      ],

      keepUnusedDataFor: 300,
    }),
    deletePinWithBody: build.mutation<
      DeletePinWithBodyResponse,
      DeletePinWithBodyArg
  >({
    query: (body) => ({
      url: `/delete-pin/`,
      method: "DELETE",
      body,
    }),
    invalidatesTags: (_result, _error, arg) => [
      { type: "Board", id: "LIST" },
      { type: "BoardPin", id: "LIST" },
      { type: "BoardPin", id: `board-${arg.board_slug}` },
    ],
  }),
    multiGarmentTryOnStream: build.query<
      MultiGarmentTryOnStreamState,
      MultiGarmentTryOnStreamArg
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        `multi-garment-try-on-${queryArgs.request_id}`,
      queryFn: (arg) => ({
        data: {
          request_id: arg.request_id,
          status: "preparing",
          message: "Processing images...",
          thoughts: [],
          partial_image: "",
          final_image: "",
          done: false,
        },
      }),
      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        await cacheDataLoaded;
        activeMultiGarmentTryOnController?.abort();
        const controller = new AbortController();
        activeMultiGarmentTryOnController = controller;
        const processEventBlock = (block: string) => {
          const eventPayload = parseSseBlock(block);
          if (!eventPayload) return false;
          const eventType = eventPayload.type;
          const eventData = getStreamEventData(eventPayload);

          if (eventType === "status") {
            const nextMessage = String(eventData.message || "").trim();
            const nextStatus = String(eventData.status || "").trim();

            updateCachedData((draft) => {
              if (nextStatus) draft.status = nextStatus;
              if (nextMessage) draft.message = nextMessage;

              if (
                nextStatus === "thinking" &&
                nextMessage &&
                draft.thoughts[draft.thoughts.length - 1] !== nextMessage
              ) {
                draft.thoughts.push(nextMessage);
              }
            });
            return false;
          }

          if (eventType === "partial") {
            const partialImage = toBase64ImageDataUrl(eventData.b64);
            if (partialImage) {
              updateCachedData((draft) => {
                draft.partial_image = partialImage;
                draft.status = "partial";
                draft.message = "Preview ready. Refining the final image...";
              });
            }
            return false;
          }

          if (eventType === "final") {
            const finalImage = toBase64ImageDataUrl(eventData.b64);
            updateCachedData((draft) => {
              if (finalImage) {
                draft.final_image = finalImage;
              }
              draft.status = "complete";
              draft.message = "Final image ready.";
              draft.done = true;
              if (!finalImage) {
                draft.error = "The final try-on image was not returned.";
              }
            });
            return true;
          }

          if (eventType === "done") {
            updateCachedData((draft) => {
              draft.done = true;
              draft.status = draft.final_image ? "complete" : draft.status;
              draft.message =
                String(eventData.message || "").trim() ||
                draft.message ||
                "Complete.";
            });
            return true;
          }

          if (eventType === "error") {
            updateCachedData((draft) => {
              draft.done = true;
              draft.status = "error";
              draft.error =
                eventData.detail ||
                eventData.message ||
                "Multi-garment try-on failed.";
            });
            return true;
          }

          return false;
        };

        try {
          const token = getAuthToken();
          const headers: Record<string, string> = {
            "Content-Type": "application/json",
          };

          if (token) {
            headers.Authorization = `Token ${token}`;
          }

          const { request_id: _requestId, ...requestBody } = arg;

          const response = await fetch(
            buildApiUrl("/multi-garment-try-on/"),
            {
              method: "POST",
              headers,
              signal: controller.signal,
              body: JSON.stringify(requestBody),
            },
          );
          if (
            response.status === 401 &&
            handleUnauthorized(token)
          ) {
            return;
          }

          if (!response.ok) {
            const errorText = await response.text().catch(() => "");

            updateCachedData((draft) => {
              draft.done = true;
              draft.status = "error";
              draft.error = errorText || `HTTP ${response.status}`;
            });

            return;
          }

          const reader = response.body?.getReader();

          if (!reader) {
            updateCachedData((draft) => {
              draft.done = true;
              draft.status = "error";
              draft.error = "Streaming is not supported by this browser.";
            });

            return;
          }

          const decoder = new TextDecoder("utf-8");
          let buffer = "";
          let shouldStop = false;

          while (!shouldStop) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const blocks = buffer.split(/\n\n|\r\n\r\n|\n(?=event:\s)/);
            buffer = blocks.pop() || "";
            for (const block of blocks) {
              shouldStop = processEventBlock(block);
              if (shouldStop) break;
            }
          }

          buffer += decoder.decode();
          if (!shouldStop && buffer.trim()) {
            shouldStop = processEventBlock(buffer);
          }
          if (!shouldStop) {
            updateCachedData((draft) => {
              draft.done = true;
              if (!draft.final_image && !draft.error) {
                draft.status = "error";
                draft.error = "The try-on stream ended before a final image arrived.";
              }
            });
          }
        } catch (error: any) {
          updateCachedData((draft) => {
            draft.done = true;

            if (error?.name !== "AbortError") {
              draft.status = "error";
              draft.error =
                error?.message || "Multi-garment try-on failed.";
            }
          });
        } finally {
          if (activeMultiGarmentTryOnController === controller) {
            activeMultiGarmentTryOnController = null;
          }

          try {
            controller.abort();
          } catch {}

          await cacheEntryRemoved;
        }
      },
      keepUnusedDataFor: 0,
    }),

    userSingleMarketplaceProductStreamCached: build.query<
      UserSingleMarketplaceProductStreamState,
      UserSingleMarketplaceProductStreamArg
    >({
      serializeQueryArgs: ({ queryArgs }) => {
        const ownerKey = queryArgs.token
          ? `guest-${queryArgs.token}`
          : `user-${queryArgs.request_key || "default"}`;
        return `single-marketplace-product-stream-${getProductLookupKey(queryArgs)}-${
          queryArgs.match_percentage ?? "none"
        }-${ownerKey}`;
      },
      queryFn: (arg) => ({
        data: {
          product_id: arg.product_id ?? 0,
          recommended_size: null,
          match_percentage:
            arg.match_percentage !== undefined && arg.match_percentage !== null
              ? Math.round(Number(arg.match_percentage))
              : null,
          match_summary: "",
          tags: [],
          done: false,
        },
      }),
      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        await cacheDataLoaded;

        const controller = new AbortController();
        const isGuestSurveyStream = Boolean(arg.token);
        if (!isGuestSurveyStream) {
          activeLoggedInMatchStreamController?.abort();
          activeLoggedInMatchStreamController = controller;
        }

        try {
          const token = isGuestSurveyStream
            ? null
            : getAuthToken();

          const headers: Record<string, string> = {
            "Content-Type": "application/json",
          };

          if (token) {
            headers.Authorization = `Token ${token}`;
          }

          const response = await fetch(
            buildApiUrl(
              isGuestSurveyStream
                ? "/public-single-marketplace-product-stream/"
                : "/user-single-marketplace-product-stream/",
            ),
            {
              method: "POST",
              headers,
              signal: controller.signal,
              body: JSON.stringify({
                ...getProductLookupBody(arg),
                ...(arg.token ? { token: arg.token } : {}),
              }),
            },
          );
          if (
            response.status === 401 &&
            handleUnauthorized(token)
          ) {
            return;
          }

          if (!response.ok) {
            const errorText = await response.text().catch(() => "");

            updateCachedData((draft) => {
              draft.done = true;
              draft.error = errorText || `HTTP ${response.status}`;
            });

            return;
          }

          const reader = response.body?.getReader();

          if (!reader) {
            updateCachedData((draft) => {
              draft.done = true;
              draft.error = "Streaming is not supported by this browser.";
            });

            return;
          }

          const decoder = new TextDecoder("utf-8");
          let buffer = "";

          while (true) {
            const { value, done } = await reader.read();

            if (done) break;

            buffer += decoder.decode(value, { stream: true });

            const blocks = buffer.split(/\n\n|\r\n\r\n|\n(?=event:\s)/);
            buffer = blocks.pop() || "";

            for (const block of blocks) {
              const eventPayload = parseSseBlock(block);

              if (!eventPayload) continue;

              const eventType = eventPayload.type;

              // Required: ignore delta events completely.
              if (eventType === "delta") continue;

              if (eventType === "partial") {
                const normalized = normalizeStreamMatchPayload(
                  eventPayload.data,
                );

                updateCachedData((draft) => {
                  if (normalized.recommended_size !== null) {
                    draft.recommended_size = normalized.recommended_size;
                  }

                  if (normalized.match_percentage !== null) {
                    draft.match_percentage = normalized.match_percentage;
                  }

                  if (typeof normalized.match_summary === "string") {
                    draft.match_summary = normalized.match_summary;
                  }

                  if (Array.isArray(normalized.tags)) {
                    draft.tags = normalized.tags;
                  }
                });

                continue;
              }

              if (eventType === "final") {
                const normalized = normalizeStreamMatchPayload(
                  eventPayload.data,
                );

                updateCachedData((draft) => {
                  if (normalized.recommended_size !== null) {
                    draft.recommended_size = normalized.recommended_size;
                  }

                  if (normalized.match_percentage !== null) {
                    draft.match_percentage = normalized.match_percentage;
                  }

                  draft.match_summary = normalized.match_summary;
                  draft.tags = normalized.tags;
                  draft.done = true;
                });

                try {
                  controller.abort();
                } catch {}
                return;
              }

              if (eventType === "done") {
                updateCachedData((draft) => {
                  draft.done = true;
                });

                try {
                  controller.abort();
                } catch {}

                return;
              }

              if (eventType === "error") {
                updateCachedData((draft) => {
                  draft.done = true;
                  draft.error =
                    eventPayload?.data?.detail ||
                    eventPayload?.data?.message ||
                    eventPayload?.detail ||
                    "Failed to stream match analysis.";
                });

                try {
                  controller.abort();
                } catch {}

                return;
              }
            }
          }

          updateCachedData((draft) => {
            draft.done = true;
          });
        } catch (error: any) {
          updateCachedData((draft) => {
            draft.done = true;
            if (error?.name !== "AbortError") {
              draft.error =
                error?.message || "Failed to stream match analysis.";
            }
          });
        } finally {
          if (
            !isGuestSurveyStream &&
            activeLoggedInMatchStreamController === controller
          ) {
            activeLoggedInMatchStreamController = null;
          }
          await cacheEntryRemoved;

          try {
            controller.abort();
          } catch {}
        }
      },

      providesTags: (_result, _error, arg) => [
        {
          type: "MarketplaceProduct",
          id: `match-stream-${getProductLookupKey(arg)}-${
            arg.match_percentage ?? "none"
          }-${arg.token ? `guest-${arg.token}` : "user"}`,
        },
      ],

      keepUnusedDataFor: 300,
    }),
    brandsList: build.query<BrandSummary[], void>({
      query: () => ({
        url: `/brands/`,
        method: "GET",
      }),
      transformResponse: (response: unknown) =>
        Array.isArray(response) ? (response as BrandSummary[]) : [],
      providesTags: (result) => [
        { type: "Brand", id: "LIST" },
        ...(result || []).map((brand) => ({
          type: "Brand" as const,
          id: `brand-${brand.slug}`,
        })),
      ],
      keepUnusedDataFor: 300,
    }),
    brandInformation: build.query<BrandInformation, BrandLookupArg>({
      query: (arg) => ({
        url: `/brand-information/`,
        method: "POST",
        body: getBrandLookupBody(arg),
      }),
      serializeQueryArgs: ({ queryArgs }) =>
        `brand-information-${getBrandLookupKey(queryArgs)}`,
      providesTags: (_result, _error, arg) => [
        {
          type: "Brand",
          id: `information-${getBrandLookupKey(arg)}`,
        },
      ],
      keepUnusedDataFor: 300,
    }),
    brandProducts: build.query<BrandProduct[], BrandLookupArg>({
      async queryFn(arg, _queryApi, _extraOptions, baseQuery) {
        if (!arg.slug && !arg.id) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: "Brand slug or ID is required.",
            },
          };
        }
        const response = await baseQuery({
          url: `/brand-products/`,
          method: "POST",
          body: getBrandLookupBody(arg),
        });
        if (response.error) {
          return {
            error: response.error,
          };
        }
        return {
          data: Array.isArray(response.data)
            ? (response.data as BrandProduct[])
            : [],
        };
      },
      serializeQueryArgs: ({ queryArgs }) =>
        `brand-products-${getBrandLookupKey(queryArgs)}`,
      forceRefetch: ({ currentArg, previousArg }) => {
        if (!currentArg) return false;
        if (currentArg.forceRefresh || !previousArg) return true;
        return (
          getBrandLookupKey(currentArg) !==
          getBrandLookupKey(previousArg)
        );
      },
      providesTags: (_result, _error, arg) => [
        {
          type: "Brand",
          id: `products-${getBrandLookupKey(arg)}`,
        },
      ],
      keepUnusedDataFor: 300,
    }),
    aestheticsListCached: build.query<AestheticSummary[], void>({
      query: () => ({
        url: `/aesthetics/`,
        method: "GET",
      }),
      serializeQueryArgs: () => "aesthetics-list",
      transformResponse: (response: unknown) =>
        Array.isArray(response)
          ? (response as AestheticSummary[])
          : [],
      providesTags: (result) => [
        {
          type: "Aesthetic",
          id: "LIST",
        },
        ...(result || []).map(
          (aesthetic) => ({
            type: "Aesthetic" as const,
            id: `aesthetic-${aesthetic.slug}`,
          }),
        ),
      ],
      keepUnusedDataFor: 300,
    }),

    aestheticInformationCached: build.query<AestheticInformation, AestheticLookupArg>({
      query: (arg) => ({
        url: `/aesthetic-information/`,
        method: "POST",
        body: getAestheticLookupBody(arg),
      }),
      serializeQueryArgs: ({queryArgs}) =>
        `aesthetic-information-${getAestheticLookupKey(
          queryArgs,
        )}`,
      forceRefetch: ({ currentArg, previousArg }) => {
        if (!currentArg) return false;
        if (currentArg.forceRefresh || !previousArg) return true;

        return (getAestheticLookupKey(currentArg) !==
          getAestheticLookupKey(previousArg));
      },
      providesTags: (_result,_error,arg,) => [
          {
            type: "Aesthetic",
            id: `information-${getAestheticLookupKey(
              arg,
            )}`,
          },
        ],

      keepUnusedDataFor: 300,
    }),

    aestheticProductsCached: build.query<AestheticProduct[],AestheticLookupArg>({
      query: (arg) => ({
        url: `/aesthetic-products/`,
        method: "POST",
        body: getAestheticLookupBody(arg),
      }),
      transformResponse: (response: unknown) =>
        Array.isArray(response)
          ? (response as AestheticProduct[])
          : [],
      serializeQueryArgs: ({queryArgs}) =>
        `aesthetic-products-${getAestheticLookupKey(
          queryArgs,
        )}`,
      forceRefetch: ({currentArg, previousArg}) => {
        if (!currentArg) return false;

        if (currentArg.forceRefresh || !previousArg) return true;

        return (getAestheticLookupKey(currentArg) 
        !== getAestheticLookupKey(previousArg));
      },
      providesTags: (_result,_error,arg) => [
          {
            type: "Aesthetic",
            id: `products-${getAestheticLookupKey(
              arg,
            )}`,
          },
        ],
      keepUnusedDataFor: 300,
    }),

    marketplaceConversationsList: build.query<
      MarketplaceConversationsListResponse,
      void
    >({
      query: () => ({
        url: `/conversations/list/`,
        method: "GET",
        params: {
          source: "marketplace",
        },
      }),

      serializeQueryArgs: () => "marketplace-conversations-list",
      providesTags: (result) => {
        const conversations = normalizeConversationsResponse(result);
        const conversationTags = conversations
          .map((conversation) => {
            const id = conversation?.id || conversation?.conversation_id;
            if (!id) return null;
            return {
              type: "MarketplaceChat" as const,
              id: `conversation-${id}`,
            };
          })
          .filter(
            (
              tag,
            ): tag is {
              type: "MarketplaceChat";
              id: string;
            } => Boolean(tag),
          );
        return [
          { type: "MarketplaceChat" as const, id: "LIST" },
          ...conversationTags,
        ];
      },
      keepUnusedDataFor: 300,
    }),

    marketplaceConversationMessages: build.query<
      MarketplaceConversationMessagesResponse,
      MarketplaceConversationMessagesArg
    >({
      query: ({ conversation_id }) => ({
        url: `/conversations/messages/`,
        method: "POST",
        body: {
          conversation_id,
        },
      }),
      serializeQueryArgs: ({ queryArgs }) =>
        `marketplace-conversation-messages-${queryArgs.conversation_id}`,
      providesTags: (_result, _error, arg) => [
        {
          type: "MarketplaceChat",
          id: `messages-${arg.conversation_id}`,
        },
        {
          type: "MarketplaceChat",
          id: `conversation-${arg.conversation_id}`,
        },
      ],
      keepUnusedDataFor: 300,
    }),

    deleteMarketplaceConversation: build.mutation<
      DeleteMarketplaceConversationResponse,
      DeleteMarketplaceConversationArg
    >({
      query: ({ conversation_id }) => ({
        url: `/delete-conversation/`,
        method: "DELETE",
        body: {
          conversation_id,
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "MarketplaceChat", id: "LIST" },
        { type: "MarketplaceChat", id: `conversation-${arg.conversation_id}` },
        { type: "MarketplaceChat", id: `messages-${arg.conversation_id}` },
      ],
    }),
  }),
  overrideExisting: false,
});

export * from "./generated.ts";

export const {
  usePaymentsConfigQuery,
  useQuickPayCreateIntentMutation,
  useQuickPayStatusQuery,
  usePublicMarketplaceSearchCachedQuery,
  useUserMarketplaceSearchCachedQuery,
  useUserMarketplaceSuggestionsCachedQuery,
  usePublicMarketplaceSuggestionsCachedQuery,
  useMarketplaceProductDetailsCachedQuery,
  useExclusiveTopProductsCachedQuery,
  usePublicTopProductsCachedQuery,
  useUserTopProductsCachedQuery,
  usePublicCollectionTopProductsCachedQuery,
  useUserCollectionTopProductsCachedQuery,
  useUserSingleMarketplaceProductCachedQuery,
  useUserSingleMarketplaceProductStreamCachedQuery,
  useLazyMultiGarmentTryOnStreamQuery,
  useMarketplaceConversationsListQuery,
  useLazyMarketplaceConversationMessagesQuery,
  useDeleteMarketplaceConversationMutation,
  useDeletePinWithBodyMutation,
  useGetPinsCachedQuery,
  useBrandsListQuery,
  useBrandInformationQuery,
  useBrandProductsQuery,
  useAestheticsListCachedQuery,
  useAestheticInformationCachedQuery,
  useAestheticProductsCachedQuery,
} = marketplaceCachedApi;

export { marketplaceCachedApi as api };
